/**
 * ActiveCapture (SK-CAPTURE, T-21) — MediaRecorder + camera, waveform,
 * live snippet, add-photo. Parts appended up to the §2.2 bound (20); the
 * add-part affordance disables at the bound with static microcopy (no
 * network round-trip). The live indicator is bound to `MediaRecorder.onstart`
 * (the honest signal, §8.4) — never a timer or an assumed-ready state.
 */

import { useEffect, useRef, useState } from "preact/hooks";
import type { BlobRecord, CaptureModality } from "../outbox/types";
import { resolveMime, roleForModality } from "../outbox/media-role";
import { MAX_PARTS, assertPartsWithinBound } from "../outbox/retry-unit";
import { sha256HexOfBlob } from "../perf/sha256";
import { makeThumbUrl, revokeThumbUrl } from "./thumb-url";
import { captureAttr } from "../shell/capture-affordance";
import { microphoneRecoveryCopy } from "../media/microphone-access";
import { Chip } from "./Chip";

const AUDIO_FINALISE_TIMEOUT_MS = 5_000;

// Object-URL helpers moved to ./thumb-url so the text-capture screen can render
// the same thumbnails from the same guarded lifecycle (it previously showed a
// bare photo count).

export interface ActiveCaptureProps {
  capture_id: string;
  onPartCaptured: (part: BlobRecord) => void;
  onSave: () => void;
  onOpenModalityPicker?: () => void;
  onMicrophoneUnavailable?: () => void;
  initialStream?: MediaStream;
  /** Injectable for tests — defaults to the real browser API. */
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
}

export function ActiveCapture(props: ActiveCaptureProps) {
  const { capture_id, onPartCaptured, onSave } = props;
  const [isLive, setIsLive] = useState(false);
  const [audioState, setAudioState] = useState<"pending" | "recording" | "unavailable">("pending");
  const [partCount, setPartCount] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [thumbs, setThumbs] = useState<string[]>([]);
  const [partError, setPartError] = useState<string | null>(null);
  const [isFinalising, setIsFinalising] = useState(false);
  const [audioAttempt, setAudioAttempt] = useState(0);
  const [audioCaptured, setAudioCaptured] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioStateRef = useRef<"pending" | "recording" | "unavailable">("pending");
  const streamRef = useRef<MediaStream | null>(null);
  const nextIndexRef = useRef(0);
  const pendingPartsRef = useRef<Promise<void>>(Promise.resolve());
  const partFailedRef = useRef(false);
  const finalisingRef = useRef(false);
  const photoFailedRef = useRef(false);
  const retryAudioRef = useRef<Blob | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const retryPhotosRef = useRef<Array<{ blob: Blob; thumbUrl: string | null }>>([]);
  const pendingPhotoCountRef = useRef(0);
  const thumbsRef = useRef<string[]>([]);

  // Elapsed-time counter (screen 2's 0:42): ticks only while live; bound to
  // the honest `isLive` signal, never assumed-running. Cleaned up on unmount.
  useEffect(() => {
    if (!isLive) return;
    const started = Date.now();
    const h = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(h);
  }, [isLive]);

  // Photo thumbnails (screen 2's grid + count badge). Object-URLs are
  // revoked on unmount to avoid leaking the blob handles.
  useEffect(() => { thumbsRef.current = thumbs; }, [thumbs]);
  useEffect(() => () => thumbsRef.current.forEach((u) => revokeThumbUrl(u)), []);

  const mmss = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;

  function stopMediaStream(stream: MediaStream | null = streamRef.current): void {
    if (!stream) return;
    if (typeof stream.getTracks === "function") {
      stream.getTracks().forEach((track) => track.stop());
    }
    if (streamRef.current === stream) streamRef.current = null;
  }

  function startRecorder(stream: MediaStream): void {
    const recorder = new MediaRecorder(stream);
    recorderRef.current = recorder;
    const chunks: BlobPart[] = [];
    audioChunksRef.current = chunks;
    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstart = () => {
      audioStateRef.current = "recording";
      setAudioState("recording");
      setIsLive(true);
    };
    recorder.start(1_000);
  }

  useEffect(() => {
    let cancelled = false;
    audioStateRef.current = "pending";
    setAudioState("pending");
    const getMedia = audioAttempt === 0 && props.initialStream
      ? () => Promise.resolve(props.initialStream!)
      : props.getUserMedia ?? ((c: MediaStreamConstraints) => navigator.mediaDevices.getUserMedia(c));

    getMedia({ audio: true })
      .then((stream) => {
        if (cancelled) {
          stopMediaStream(stream);
          return;
        }
        streamRef.current = stream;
        startRecorder(stream);
      })
      .catch(() => {
        stopMediaStream();
        audioStateRef.current = "unavailable";
        setAudioState("unavailable");
        // Permission denied / no microphone: stays not-live. Photo/upload
        // ingress via ModalityPicker still works without a mic.
      });

    return () => {
      cancelled = true;
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      stopMediaStream();
    };
    // capture_id identifies the session; re-running on change is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capture_id, audioAttempt]);

  async function addPart(modality: CaptureModality, blob: Blob): Promise<void> {
    if (nextIndexRef.current >= MAX_PARTS) throw new Error("capture_part_limit");
    const part_index = nextIndexRef.current;
    assertPartsWithinBound(part_index + 1);
    const mime = resolveMime(blob);
    // sha256 off the save path (§T-27): computed at part-capture time, not at save.
    const sha256 = await sha256HexOfBlob(blob);
    nextIndexRef.current = part_index + 1;
    const part: BlobRecord = {
      capture_id,
      part_index,
      sha256,
      mime,
      role: roleForModality(modality),
      size: blob.size,
      blob,
    };
    setPartCount(part_index + 1);
    onPartCaptured(part);
  }

  function enqueuePart(modality: CaptureModality, blob: Blob): Promise<void> {
    const queued = pendingPartsRef.current.then(() => addPart(modality, blob));
    // Keep the serial queue usable after one bad attachment. The caller owns the
    // user-facing failure because only it knows whether a newer attempt replaced it.
    pendingPartsRef.current = queued.catch(() => undefined);
    return queued;
  }

  function handleAddPhoto(evt: Event): void {
    const input = evt.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      const recorderHasAudio = audioStateRef.current !== "unavailable" || retryAudioRef.current !== null;
      const photoLimit = MAX_PARTS - (recorderHasAudio ? 1 : 0);
      if (nextIndexRef.current + pendingPhotoCountRef.current + retryPhotosRef.current.length >= photoLimit) {
        setPartError("This capture already has the maximum number of attachments.");
        input.value = "";
        return;
      }
      pendingPhotoCountRef.current += 1;
      const thumbUrl = makeThumbUrl(file);
      if (thumbUrl) setThumbs((prev) => [...prev, thumbUrl]);
      void enqueuePart("photo", file).catch(() => {
        retryPhotosRef.current.push({ blob: file, thumbUrl });
        photoFailedRef.current = true;
        partFailedRef.current = true;
        setPartError("That attachment couldn't be prepared. Tap Save again to retry it.");
      }).finally(() => {
        pendingPhotoCountRef.current -= 1;
      });
    }
    input.value = "";
  }

  async function handleSave(): Promise<void> {
    if (finalisingRef.current) return;
    finalisingRef.current = true;
    setIsFinalising(true);
    try {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        const finalChunks = audioChunksRef.current;
        const audioBlob = await new Promise<Blob>((resolve, reject) => {
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            resolve(new Blob(finalChunks, { type: recorder.mimeType || "audio/webm" }));
          };
          const timeout = setTimeout(() => {
            const retained = new Blob(finalChunks, { type: recorder.mimeType || "audio/webm" });
            if (retained.size > 0) finish();
            else {
              settled = true;
              const stream = streamRef.current;
              if (stream) {
                try {
                  startRecorder(stream);
                  reject(new Error("audio_finalise_retry"));
                  return;
                } catch { /* Fall through to a hard finalisation error. */ }
              }
              stopMediaStream();
              reject(new Error("audio_finalise_error"));
            }
          }, AUDIO_FINALISE_TIMEOUT_MS);
          recorder.onstop = finish;
          recorder.onerror = () => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            stopMediaStream();
            reject(new Error("audio_finalise_error"));
          };
          try { recorder.requestData(); } catch { /* Some inactive implementations refuse it. */ }
          recorder.stop();
          setIsLive(false);
        });
        if (audioBlob.size > 0) retryAudioRef.current = audioBlob;
      }
      audioStateRef.current = "unavailable";
      setAudioState("unavailable");
      stopMediaStream();
      if (retryAudioRef.current) {
        try {
          await enqueuePart("audio", retryAudioRef.current);
          retryAudioRef.current = null;
          setAudioCaptured(true);
          partFailedRef.current = photoFailedRef.current;
          if (!photoFailedRef.current) setPartError(null);
        } catch {
          partFailedRef.current = true;
          setPartError("The recording couldn't be prepared. Tap Save again to retry it.");
          return;
        }
      }
      await pendingPartsRef.current;
      for (const failed of [...retryPhotosRef.current]) {
        try {
          await enqueuePart("photo", failed.blob);
          retryPhotosRef.current = retryPhotosRef.current.filter((candidate) => candidate !== failed);
        } catch {
          partFailedRef.current = true;
          setPartError("That attachment couldn't be prepared. Tap Save again to retry it.");
          return;
        }
      }
      photoFailedRef.current = retryPhotosRef.current.length > 0;
      partFailedRef.current = photoFailedRef.current;
      if (!partFailedRef.current) setPartError(null);
      if (partFailedRef.current) return;
      onSave();
    } catch (error) {
      partFailedRef.current = true;
      if (!(error instanceof Error && error.message === "audio_finalise_retry")) stopMediaStream();
      setPartError(
        error instanceof Error && error.message === "audio_finalise_retry"
          ? "The recording couldn't be finalised, so recording restarted. Your photos are still here; tap Save again."
          : error instanceof Error && error.message.startsWith("audio_finalise_")
            ? "The recording couldn't be recovered. Your photos are still here; record again before saving."
          : "That attachment couldn't be prepared. Add it again before saving.",
      );
    } finally {
      finalisingRef.current = false;
      setIsFinalising(false);
    }
  }

  const atBound = partCount + retryPhotosRef.current.length >= MAX_PARTS - (audioState !== "unavailable" || retryAudioRef.current ? 1 : 0);
  const hasRetainedAudio = audioCaptured || retryAudioRef.current !== null;
  const microphoneBlocked = audioState === "unavailable" && !hasRetainedAudio;

  return (
    <section data-skin-slot="SK-CAPTURE" data-testid="active-capture">
      <header class="capture-header">
        <span class="rec-pill" data-live={isLive} data-testid="capture-live-indicator" aria-label={isLive ? "Recording" : undefined}>
          <span class="rec-dot" aria-hidden="true" />
          {isLive ? (
            <>
              <span aria-hidden="true">REC</span>
              <span class="visually-hidden">Recording</span>
            </>
          ) : (
            audioState === "unavailable"
              ? hasRetainedAudio ? "Recording ready" : "Microphone blocked"
              : "Starting…"
          )}
        </span>
        <span class="capture-timer" data-testid="capture-timer">{mmss}</span>
      </header>

      <div class="capture-waveform" data-testid="capture-waveform" data-live={isLive} aria-hidden="true">
        {Array.from({ length: 24 }).map((_, i) => (
          <span key={i} class="wave-bar" style={{ animationDelay: `${(i % 12) * 0.08}s` }} />
        ))}
      </div>

      {thumbs.length > 0 && (
        <div class="capture-thumbs" data-testid="capture-thumbs">
          {thumbs.map((src, i) => (
            <span key={i} class="capture-thumb" style={{ backgroundImage: `url(${src})` }} />
          ))}
          <span class="capture-thumb-badge">{thumbs.length}</span>
        </div>
      )}

      <p class="capture-part-count" data-testid="capture-part-count">
        {partCount} / {MAX_PARTS}
      </p>
      {partError && <p class="capture-part-error" data-testid="capture-part-error">{partError}</p>}

      {microphoneBlocked && (
        <aside data-testid="active-microphone-recovery" role="alert">
          <strong>Voice recording cannot start without microphone access</strong>
          <p>{microphoneRecoveryCopy()}</p>
          <Chip
            data-testid="active-microphone-try-again"
            onClick={() => setAudioAttempt((attempt) => attempt + 1)}
          >
            Try microphone again
          </Chip>
          {props.onMicrophoneUnavailable && (
            <Chip
              data-testid="active-microphone-use-other"
              onClick={props.onMicrophoneUnavailable}
            >
              Use Text or Upload
            </Chip>
          )}
        </aside>
      )}

      <div class="capture-actions">
        <label class="capture-photo-btn" data-testid="capture-add-photo-label" data-disabled={atBound}>
          <input
            type="file"
            accept="image/*"
            capture={captureAttr()}
            data-testid="capture-add-photo"
            disabled={atBound || isFinalising || microphoneBlocked}
            onChange={handleAddPhoto}
          />
          {atBound ? "Photo limit reached (final part reserved for audio)" : "Add photo"}
        </label>
        <button type="button" class="capture-stop-btn" data-testid="capture-save" disabled={isFinalising || audioState === "pending" || microphoneBlocked} onClick={handleSave}>
          {isFinalising ? "Saving…" : "Stop and save"}
        </button>
      </div>

      {props.onOpenModalityPicker && (
        <button
          type="button"
          class="capture-more-btn"
          data-testid="capture-more-options"
          onContextMenu={(e) => {
            e.preventDefault();
            props.onOpenModalityPicker?.();
          }}
        >
          More options
        </button>
      )}
    </section>
  );
}
